import { useCallback, useEffect, useState } from 'react';
import { FiCopy, FiEdit2, FiEye, FiPlus, FiShield, FiTrash2 } from 'react-icons/fi';
import { PermissionMatrixEditor } from '../../components/PermissionMatrixEditor';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { accessProfilesApi } from '../../services/admin';
import type { AccessProfile, PermissionItem } from '../../types';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

type ProfileForm = { name: string; description: string; permissions: PermissionItem[] };
const emptyForm = (): ProfileForm => ({ name: '', description: '', permissions: [] });

/** Operacoes de perfil num escopo: perfis globais (equipe LTQ) ou de uma empresa (dono). */
export interface PerfisApi {
    list: () => Promise<AccessProfile[]>;
    create: (data: ProfileForm) => Promise<unknown>;
    update: (id: string, data: Partial<ProfileForm>) => Promise<unknown>;
    duplicate: (id: string) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;
}

function tipoDoPerfil(p: AccessProfile): string {
    if (p.isSystem) return 'Padrão';
    if (p.global === false) return 'Desta empresa';
    return 'Customizado';
}

/**
 * Lista de perfis com criar, editar, duplicar e excluir. Usada pela equipe LTQ
 * (perfis globais) e pelo dono da empresa em Configurações (perfis da empresa,
 * com os padrões só para consulta e duplicação).
 */
export function PerfisDeAcessoPainel({ api }: { api: PerfisApi }) {
    const [profiles, setProfiles] = useState<AccessProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<AccessProfile | null>(null);
    const [form, setForm] = useState<ProfileForm>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<AccessProfile | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [rowBusy, setRowBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setProfiles(await api.list()); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    // Perfil que quem esta olhando nao pode alterar abre so para consulta.
    const somenteLeitura = !!editing && editing.editavel === false;

    const openNew = () => { setEditing(null); setForm(emptyForm()); setFormError(null); setModalOpen(true); };
    const openEdit = (p: AccessProfile) => {
        setEditing(p);
        setForm({ name: p.name, description: p.description ?? '', permissions: p.permissions.map(perm => ({ ...perm })) });
        setFormError(null);
        setModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (somenteLeitura) return;
        setSaving(true);
        setFormError(null);
        try {
            const payload = { name: form.name, description: form.description, permissions: form.permissions };
            if (editing) {
                await api.update(editing.id, payload);
            } else {
                await api.create(payload);
            }
            setModalOpen(false);
            load();
        } catch (err: any) { setFormError(err.message); }
        finally { setSaving(false); }
    };

    const handleDuplicate = async (p: AccessProfile) => {
        setRowBusy(p.id);
        try {
            await api.duplicate(p.id);
            load();
        } catch (err: any) {
            alert(err.message);
        } finally {
            setRowBusy(null);
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await api.remove(deleting.id);
            setDeleting(null);
            load();
        } catch (err: any) {
            setDeleteError(err.message);
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button onClick={openNew}><FiPlus size={16} style={{ marginRight: '0.5rem' }} />Novo Perfil</Button>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={styles.usersSection}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Perfil</th>
                                <th>Descrição</th>
                                <th>Usuários</th>
                                <th>Tipo</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {profiles.map((p) => {
                                const podeEditar = p.editavel !== false;
                                return (
                                    <tr key={p.id}>
                                        <td>
                                            <div className={styles.userInfo}>
                                                <div className={styles.userAvatar}><FiShield size={14} /></div>
                                                <div className={styles.userDetails}>
                                                    <div className={styles.name}>{p.name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{p.description || '—'}</td>
                                        <td>{p.userCount}</td>
                                        <td>
                                            <span className={`${styles.roleBadge} ${p.isSystem ? styles.admin : styles.user}`}>
                                                {tipoDoPerfil(p)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <button className={styles.actionBtn} onClick={() => openEdit(p)} title={podeEditar ? 'Editar' : 'Ver permissões'}>
                                                    {podeEditar ? <FiEdit2 size={14} /> : <FiEye size={14} />}
                                                </button>
                                                <button className={styles.actionBtn} disabled={rowBusy === p.id} onClick={() => handleDuplicate(p)} title="Duplicar">
                                                    <FiCopy size={14} />
                                                </button>
                                                <button
                                                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                                    onClick={() => { setDeleteError(null); setDeleting(p); }}
                                                    title={p.isSystem || !podeEditar ? 'Este perfil não pode ser excluído aqui' : 'Excluir'}
                                                    disabled={p.isSystem || !podeEditar}
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {profiles.length === 0 && (
                                <tr><td colSpan={5} className={styles.emptyRow}>Nenhum perfil de acesso cadastrado ainda.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={modalOpen}
                onClose={() => { setModalOpen(false); setFormError(null); }}
                title={editing ? (somenteLeitura ? editing.name : `Editar: ${editing.name}`) : 'Novo Perfil de Acesso'}
            >
                <form onSubmit={handleSubmit}>
                    {somenteLeitura && (
                        <p style={{ marginTop: 0, fontSize: 13, color: 'var(--color-gray-500)' }}>
                            Perfil padrão: só para consulta. Para ajustar, use Duplicar e edite a cópia.
                        </p>
                    )}
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <Input
                                label="Nome do Perfil"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="Ex: Financeiro Sênior"
                                disabled={editing?.isSystem || somenteLeitura}
                            />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <Input
                                label="Descrição"
                                value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="Para que serve este perfil..."
                                disabled={somenteLeitura}
                            />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={styles.formLabel}>Permissões</label>
                            <PermissionMatrixEditor
                                value={form.permissions}
                                onChange={(next) => setForm(p => ({ ...p, permissions: next }))}
                                disabled={somenteLeitura}
                            />
                        </div>
                    </div>
                    {formError && <p className={styles.formError}>{formError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setModalOpen(false); setFormError(null); }}>
                            {somenteLeitura ? 'Fechar' : 'Cancelar'}
                        </Button>
                        {!somenteLeitura && (
                            <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Perfil'}</Button>
                        )}
                    </div>
                </form>
            </Modal>

            <Modal isOpen={!!deleting} onClose={() => { setDeleting(null); setDeleteError(null); }} title="Excluir Perfil de Acesso">
                <div style={{ padding: '0.5rem 0 1rem' }}>
                    <p>Tem certeza que deseja excluir o perfil <strong>{deleting?.name}</strong>?</p>
                    <p style={{ marginTop: '0.5rem', fontSize: 13, color: 'var(--text-secondary, #666)' }}>
                        Esta ação é irreversível. Perfis atribuídos a algum usuário não podem ser excluídos: troque o perfil deles primeiro.
                    </p>
                    {deleteError && <p className={styles.formError}>{deleteError}</p>}
                </div>
                <div className={styles.modalFooter}>
                    <Button type="button" onClick={() => { setDeleting(null); setDeleteError(null); }}>Cancelar</Button>
                    <Button type="button" disabled={deleteBusy} onClick={confirmDelete}>
                        {deleteBusy ? 'Excluindo...' : 'Confirmar Exclusão'}
                    </Button>
                </div>
            </Modal>
        </>
    );
}

export function AdminAccessProfiles() {
    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Perfis de Acesso</h2>
                    <p>Perfis globais: os padrão e os criados pela equipe LTQ, que toda empresa pode atribuir. Cada dono também cria os perfis da própria empresa.</p>
                </div>
            </div>
            <PerfisDeAcessoPainel api={accessProfilesApi} />
        </AdminLayout>
    );
}
